// The record (E01-S6-T3; contracts 1.0 section 9): audit events, supervisory records, usage, egress
// manifests and drift reports. Every shape here is content-free: codes, ids, counts and times —
// never a screen value, a token, a band or a transcript.
import { z } from "zod";
import { Citation } from "./citation.js";
import { BAND_RE, TOKEN_RE } from "./frame/values.js";
import { ContextClass, ModelRole } from "./model.js";
import { Count, Id, IsoUtc, NonEmpty, strictObject, Ulid, version } from "./primitives.js";
import { RefusalReason } from "./refusal.js";
export const HASH_RE = /^[0-9a-f]{64}$/;
/** A sha256 digest as 64 lower-case hex characters. How it is computed is E16-S1's. */
export const Sha256Hex = z.string().regex(HASH_RE, "expected 64 lower-case hex characters");
/** A machine code with no whitespace: provider and model names, matcher kinds. Cannot hold prose. */
const Code = z
    .string()
    .regex(/^[A-Za-z0-9][A-Za-z0-9._:/@-]{0,127}$/, "expected a code: no whitespace, at most 128 characters");
/** `walkthrough.step.verified`, `answer.refused`, `egress.model`: lower-case dotted words. */
export const AuditAction = z
    .string()
    .regex(/^[a-z][a-z0-9-]*(\.[a-z0-9-]+)*$/, "expected a lower-case dotted action");
/** The cap on an audit detail string: codes only, no whitespace, at most 64 characters (contracts 1.0 section 9). */
export const AUDIT_DETAIL_MAX = 64;
const unanchored = (re) => new RegExp(re.source.replace(/^\^/, "").replace(/\$$/, ""));
const TOKEN_ANYWHERE = unanchored(TOKEN_RE);
const BAND_ANYWHERE = unanchored(BAND_RE);
/** Structured facts only: a flat value, never an object or array; a string is a code and never holds a token or a band. */
export const AuditDetailValue = z.union([
    z
        .string()
        .regex(/^\S{1,64}$/, "an audit detail string is a code: no whitespace, at most 64 characters")
        .refine((s) => !TOKEN_ANYWHERE.test(s), "an audit detail never carries a session token")
        .refine((s) => !BAND_ANYWHERE.test(s), "an audit detail never carries a band"),
    z.number(),
    z.boolean(),
]);
export const AuditEvent = strictObject({
    v: version(1),
    id: Ulid,
    at: IsoUtc,
    tenantId: Id,
    orgId: NonEmpty,
    actor: strictObject({ kind: z.enum(["user", "assistant", "system"]), id: NonEmpty }),
    action: AuditAction,
    target: strictObject({ kind: NonEmpty, id: NonEmpty }).optional(),
    detail: z.record(Id, AuditDetailValue),
    prevHash: Sha256Hex,
    hash: Sha256Hex, // sha256 chain per tenant; head anchored every ten minutes (D-73)
}).superRefine((a, ctx) => {
    if (a.action === "answer.refused" && !RefusalReason.safeParse(a.detail["reason"]).success) {
        ctx.addIssue({
            code: "custom",
            message: "answer.refused carries detail.reason from RefusalReason (D-07)",
            path: ["detail", "reason"],
        });
    }
});
export const RecordEntryKind = z.enum([
    "disclosure",
    "screen",
    "step",
    "decision",
    "nudge",
    "beat",
    "note",
    "refusal",
    "escalation",
    "navigation",
    "perception",
    "knowledge-check", // ADR-023: an attempt made during a walkthrough; ids and score, never question text
]);
export const RecordEntry = strictObject({
    at: IsoUtc,
    kind: RecordEntryKind,
    text: NonEmpty,
    how: z.enum(["verified", "passed", "inferred", "asked", "acknowledged"]).optional(),
    reason: RefusalReason.optional(), // why a refusal happened (D-07)
    citation: Citation.optional(),
}).superRefine((e, ctx) => {
    if ((e.kind === "refusal") !== (e.reason !== undefined))
        ctx.addIssue({
            code: "custom",
            message: "a refusal entry carries its reason, and only a refusal does (D-07)",
            path: ["reason"],
        });
});
/** A walkthrough's short reference, carried by the drafted note (D-80): `WP-8F2K3M`. */
export const WALKTHROUGH_REF_RE = /^WP-[0-9A-HJKMNP-TV-Z]{6}$/;
export const WalkthroughRef = z
    .string()
    .regex(WALKTHROUGH_REF_RE, "expected WP- and six Crockford base32 characters");
/** How a walkthrough ended: WalkthroughState["outcome"] (section 3). sop.ts reuses this shape. */
export const WalkthroughOutcome = strictObject({
    kind: z.enum(["disposition", "escalation"]),
    code: NonEmpty.optional(),
    escalateTo: NonEmpty.optional(),
    at: IsoUtc,
}).superRefine((o, ctx) => {
    if (o.kind === "disposition" && (o.code === undefined || o.escalateTo !== undefined))
        ctx.addIssue({
            code: "custom",
            message: "a disposition outcome carries code and no escalateTo",
        });
    if (o.kind === "escalation" && (o.escalateTo === undefined || o.code !== undefined))
        ctx.addIssue({
            code: "custom",
            message: "an escalation outcome carries escalateTo and no code",
        });
});
export const RecordOutcome = WalkthroughOutcome;
export const SupervisoryRecord = strictObject({
    v: version(1),
    ref: WalkthroughRef,
    walkthroughId: Ulid,
    sop: strictObject({ id: Id, version: NonEmpty }),
    flow: Id.optional(),
    userId: NonEmpty,
    orgId: NonEmpty,
    startedAt: IsoUtc,
    endedAt: IsoUtc.optional(),
    outcome: RecordOutcome.optional(),
    entries: z.array(RecordEntry),
    chain: strictObject({ headHash: Sha256Hex, anchoredAt: IsoUtc }),
});
/** What is billed (D-82). */
export const UsageEvent = strictObject({
    v: version(1),
    at: IsoUtc,
    tenantId: Id,
    orgId: NonEmpty,
    userId: NonEmpty,
    sessionId: NonEmpty,
    mode: z.enum(["text", "voice", "avatar"]),
    seconds: z.number().nonnegative(),
});
/** Bytes per context class, and the classes blocked. Never content: there is no field that could hold it. */
export const EgressManifest = strictObject({
    at: IsoUtc,
    role: ModelRole,
    provider: Code,
    model: Code,
    deployment: z.enum(["external", "private"]),
    classes: z.partialRecord(ContextClass, Count),
    blocked: z.array(ContextClass),
});
/** Content-free counters of where the atlas no longer fits the application (D-101). */
export const DriftReport = strictObject({
    app: Id,
    variant: Id,
    atlasVersion: NonEmpty,
    at: IsoUtc,
    screens: z.array(strictObject({
        id: Id,
        missing: z.array(Id),
        weakened: z.array(strictObject({ field: Id, matchedBy: Code })),
        unknownVisits: Count,
    })),
});
//# sourceMappingURL=record.js.map